import { setCustomElementMetadata } from "@/lib/customElements";
import { useEffect, useState } from "react";
import { ReportingService } from "../../services/reporting-service";
import { Input } from "../base/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../base/select";

const REPORT_TYPES = [
  { value: "lead", label: "Lead Report" },
  { value: "quote", label: "Quote Report" },
  { value: "generalFilter", label: "General Filter Report" },
];

interface ReportingDownloadProps {
  apiUrl: string;
  apiStage: string;
}

function getDefaultDates() {
  const now = new Date();
  const firstDayPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDayPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  return {
    start: toISO(firstDayPrevMonth),
    end: toISO(lastDayPrevMonth),
  };
}

export function ReportingDownload({
  apiUrl,
  apiStage,
}: ReportingDownloadProps) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportType, setReportType] = useState(REPORT_TYPES[0].value);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const { start, end } = getDefaultDates();
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const service = new ReportingService(apiUrl, apiStage);
      let data: string;
      if (reportType === "lead") {
        data = await service.getLeadReport(startDate, endDate);
      } else if (reportType === "quote") {
        data = await service.getQuoteReport(startDate, endDate);
      } else {
        data = await service.getGeneralFilterReport(startDate, endDate);
      }
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportType}-report.csv`; // TODO: Add a timestamp to the filename
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <Input
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        placeholder="Start Date"
        style={{ width: 140 }}
      />
      <Input
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        placeholder="End Date"
        style={{ width: 140 }}
      />
      <Select value={reportType} onValueChange={setReportType}>
        <SelectTrigger style={{ width: 180 }}>
          <SelectValue placeholder="Report Type" />
        </SelectTrigger>
        <SelectContent>
          {REPORT_TYPES.map((rt) => (
            <SelectItem key={rt.value} value={rt.value}>
              {rt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        onClick={handleDownload}
        disabled={loading || !startDate || !endDate}
        style={{ height: 40, minWidth: 120 }}
      >
        {loading ? "Downloading..." : "Download"}
      </button>
    </div>
  );
}

ReportingDownload.displayName = "ReportingDownload";

setCustomElementMetadata({
  component: ReportingDownload,
  tagName: "raes-reporting-download",
  observedAttributes: {
    "api-url": (value: string) => ({ value, prop: "apiUrl" }),
    "api-stage": (value: string) => ({ value, prop: "apiStage" }),
  },
});
